/** @format */

import React, { useEffect, useState } from "react";
import CreateResourceForm, {
  ResourceForm,
} from "../createResourceForm/CreateResourceForm";
import { Button, Alert, Spinner } from "react-bootstrap";
import AuthFlag from "../../auth/AuthFlag";
import { ReactComponent as ExclamationTriangle } from "bootstrap-icons/icons/exclamation-triangle.svg";
import { useCreateResourceReferenceMutation } from "../../../app/services/resourcesApi";
import { useLocale } from "../../../i18n/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import ResourceLinks from "../resourceLinks/ResourceLinks";
import { useTranslation } from "react-i18next";
import {
  QualificationId,
  ResourceLink,
  ResourceReference,
  ResourceType,
  Subject,
} from "../../../types";
import HelpLink from "../../helpLink/HelpLink";
import ResourcePageUI from "../resourcePage/ResourcePageUI";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import UploadConfirmationModal from "./UploadConfirmationModal";

// The string values are used to get the current stages title from i18n
enum stages {
  StageOne = "stageOne",
  StageTwo = "stageTwo",
  StageThree = "stageThree",
}

const Breadcrumb: React.FC<{
  stage: stages;
  setStage: (stage: stages) => void;
}> = ({ stage, setStage }) => {
  const { t: _ } = useTranslation();

  const stagesArr = Object.values(stages);
  const currStageIndex = stagesArr.indexOf(stage);

  return (
    <nav aria-label="breadcrumb">
      <ol className="breadcrumb">
        {stagesArr.map((currStage, index) => {
          if (index < currStageIndex) {
            return (
              <li
                key={index}
                onClick={() => setStage(currStage)}
                className={"breadcrumb-item"}
                aria-current="page"
                role="button"
                data-testid={currStage}
                style={{ cursor: "pointer" }}
              >
                {_("uploadOther." + currStage)}
              </li>
            );
          } else if (index === currStageIndex) {
            return (
              <li
                key={index}
                className={"breadcrumb-item active"}
                aria-current="page"
              >
                {_("uploadOther." + currStage)}
              </li>
            );
          } else return undefined;
        })}
      </ol>
    </nav>
  );
};

const UploadOther: React.FC = () => {
  const locale = useLocale();
  const { t: _ } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [createResourceFormData, setCreateResourceFormData] =
    useState<ResourceForm>();
  const [stage, setStage] = useState<stages>(stages.StageOne); // used to control which form is shown
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [resourceType] = useState<ResourceType>(
    (searchParams.get("type") as ResourceType) || "",
  );
  const [resourceRef, setResourceRef] = useState<ResourceReference>();
  const [createResourceRef] = useCreateResourceReferenceMutation();

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  useEffect(() => {
    const resourceTypes: ResourceType[] = [
      "BL",
      "KO",
      "SP",
      "CR",
      "DT",
      "QB",
      "OER",
      "EWT",
      "OR",
      "TP"
    ];
    if (!resourceTypes.includes(resourceType)) {
      navigate("/select-resource");
    }
  }, [navigate, resourceType]);

  const handleResourcePreview = (links: ResourceLink[]) => {
    if (!createResourceFormData) {
      setError("no form data");
      return;
    }

    const subjectId = createResourceFormData.subject?.id ?? "";

    setResourceRef({
      Code: createResourceFormData.code,
      SpecItem: createResourceFormData.specItem,
      Branding: createResourceFormData.branding,
      Object: {
        title: createResourceFormData.resourceTitle[locale],
        specItems: [],
        acknowledgements: createResourceFormData.acknowledgements,
        tags: [],
        links: links ?? [],
        levels: createResourceFormData.levels,
        authors: createResourceFormData.author,
        fundedBy: createResourceFormData.fundedBy,
        description: undefined,
        synopsis: undefined,
      },
      Qualification: createResourceFormData.qualification as QualificationId,
      Status: "draft",
      Type: resourceType,
      Subject: createResourceFormData.subject as Subject,
      Keystage: 4,
      SK: `QUALIFICATION:${createResourceFormData.qualification}#TYPE:${resourceType}#CODE:${createResourceFormData?.code}`.toUpperCase(),
      Locale: locale,
      PK: `REF#SUBJECT:${subjectId}#${locale}`.toUpperCase(),
    });

    setStage(stages.StageThree);
  };

  const handleCreateResource = async () => {
    setShowConfirmationModal(false)

    if (resourceRef === undefined || createResourceFormData === undefined) {
      setError(`ref undefined`);
      return;
    }

    if (isFormSubmitted) return;
    setIsFormSubmitted(true);

    await createResourceRef({ resourceReferenceItem: resourceRef })
      .then((res) => {
        const resErr = res as { error: FetchBaseQueryError };
        if (resErr.error !== undefined) {
          setError((resErr.error.data as any).message ?? "UNKNOWN ERROR");
          setIsFormSubmitted(false);
          return;
        }

        navigate(
          `/${createResourceFormData?.subject?.title[locale]}/r/${
            createResourceFormData.code
          }/${resourceType}/${createResourceFormData.qualification.toUpperCase()}`,
        );
      })
      .catch((error) => {
        if (typeof error === "string") {
          setError(error);
        } else if (typeof error === "object") {
          setError(JSON.stringify(error));
        } else {
          setError("UNKNOWN ERROR");
        }

        setIsFormSubmitted(false);
        return;
      });
  };

  return (
    <AuthFlag authGroup={["wjec", "admin"]} fallback={<h1>{_("Unauthorised")}</h1>}>
      {isFormSubmitted ? (
        <div className="position-absolute top-50 start-50 translate-middle">
          <Spinner />
        </div>
      ) : (
        <div className="d-flex flex-column position-relative">
          <div className="d-flex flex-row">
            <h3>{_("uploadOther.title")}</h3>
            <HelpLink
              position="inherit"
              className="help-link pe-0 ms-auto"
              locationId="new-resource"
            />
          </div>

          {!isFormSubmitted && (
            <Breadcrumb
              setStage={(stage) => {
                setStage(stage);
                setIsFormSubmitted(false);
                setError(undefined);
              }}
              stage={stage}
            />
          )}

          {error ? (
            <Alert className="my-3" data-testid="errorMsg" variant="danger">
              <ExclamationTriangle className="text-danger" />{" "}
              {_("uploadOther.fail")}: {error} <br />
              {_("uploadOther.retry")}
            </Alert>
          ) : stage === stages.StageOne ? (
            <CreateResourceForm
              propDefaultResourceForm={createResourceFormData} // uses submitted form data so next stage can have back button
              buttonText={_("Next")}
              uploadFinal={(formData: ResourceForm) => {
                setCreateResourceFormData(formData);
                setStage(stages.StageTwo);
              }}
            />
          ) : stage === stages.StageTwo ? (
            <div className="resource-links-container">
              <ResourceLinks
                buttonText={_("Next")}
                propDefaultResourceLinks={resourceRef?.Object.links ?? []}
                saveLinks={(links) => {
                  handleResourcePreview(links);
                }}
              />
              <Button onClick={() => setStage(stages.StageOne)} className="navigation-button back-button me-2 mb-3">
                {_("Back")}
              </Button>
            </div>
          ) : (
            stage === stages.StageThree && (
              <>
                {resourceRef !== undefined && (
                  <div className="my-3">
                    <div className="card">
                      <div className="card-body">
                      <ResourcePageUI resourceRef={resourceRef} preview={true} />
                      </div>
                    </div>
                  </div>
                )}
                <UploadConfirmationModal 
                  show={showConfirmationModal}
                  keyData={{
                    code: resourceRef?.Code,
                    subject: resourceRef?.Subject,
                    qualification: resourceRef?.Qualification,
                    type: resourceRef?.Type,
                  }}
                  onHide={() => setShowConfirmationModal(false)}
                  onAccept={() => handleCreateResource()}
                />

                <div className="nav-btn-container mb-3">
                  <Button
                    onClick={() => setShowConfirmationModal(true)}
                    disabled={isFormSubmitted}
                    className="navigation-button"
                  >
                    {_("Create Resource")}
                  </Button>
                  <Button onClick={() => setStage(stages.StageTwo)} className="navigation-button back-button me-2"> 
                    {_("Back")}
                  </Button>
                </div>
              </>
            )
          )}
        </div>
      )}
    </AuthFlag>
  );
};

export default UploadOther;
